import { HttpClient } from '@angular/common/http';
import { handleError } from '@app/shared/services/common-function';
import { environment } from '@env/environment';
import { Observable, catchError, map, retry, timeout } from 'rxjs';
import { ApiClientConfig } from '../api-client.config';
import { MediaApiModels } from './api.media.models';

const MEDIA_LIBRARY_PATH = '/media/libraries';

export class MediaApi {
  private apiUrl: string = environment.API_URL;

  constructor(
    public http: HttpClient,
    public config: ApiClientConfig
  ) {}

  getAlbumList(libraryId: string): Observable<MediaApiModels.LibraryResponse> {
    const params = libraryId ? '&target_id=' + libraryId : '';
    return this.http.get<MediaApiModels.LibraryResponse>(`${this.apiUrl}${MEDIA_LIBRARY_PATH}?${params}`).pipe(
      timeout(this.config.responseTimeout),
      retry(1),
      map((res: any) => {
        return res;
      }),
      catchError(handleError)
    );
  }

  getMediaList(albumId: string, libraryId: string): Observable<MediaApiModels.AlbumWithMedia> {
    const params = libraryId ? '&target_id=' + libraryId : '';
    return this.http
      .get<MediaApiModels.AlbumWithMedia>(`${this.apiUrl}${MEDIA_LIBRARY_PATH}/${albumId}?${params}`)
      .pipe(
        timeout(this.config.responseTimeout),
        retry(1),
        map((res: any) => {
          return res;
        }),
        catchError(handleError)
      );
  }

  getRecentImages(libraryId: string): Observable<MediaApiModels.Media[]> {
    const params = libraryId ? '?target_id=' + libraryId : '';
    return this.http.get<MediaApiModels.Media[]>(`${this.apiUrl}${MEDIA_LIBRARY_PATH}/images${params}`).pipe(
      timeout(this.config.responseTimeout),
      retry(1),
      map((res: any) => {
        return res;
      }),
      catchError(handleError)
    );
  }

  uploadFilesToAlbum(
    albumId: string,
    files: FormData,
    libraryId: string
  ): Observable<MediaApiModels.UploadedMediaList> {
    const albumIdPrams = albumId ? '&library_id=' + albumId : '';
    const libraryIdPrams = libraryId ? '&target_id=' + libraryId : '';
    return this.http
      .post<MediaApiModels.UploadedMediaList>(
        `${this.apiUrl}${MEDIA_LIBRARY_PATH}/uploadFiles?${albumIdPrams}${libraryIdPrams}`,
        files
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(handleError)
      );
  }

  uploadFileToAlbum(
    albumId: string,
    file: FormData,
    fileDescription: string,
    libraryId: string
  ): Observable<MediaApiModels.Media[]> {
    const namePrams = fileDescription ? '&description=' + fileDescription : '';
    const albumIdPrams = albumId ? '&library_id=' + albumId : '';
    const libraryIdPrams = libraryId ? '&target_id=' + libraryId : '';
    return this.http
      .post<MediaApiModels.Media[]>(
        `${this.apiUrl}${MEDIA_LIBRARY_PATH}/uploadFile?${namePrams}${albumIdPrams}${libraryIdPrams}`,
        file
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(handleError)
      );
  }

  downloadMedia(media: MediaApiModels.Media) {
    this.http.get(this.apiUrl + media.uris.original, { responseType: 'blob' as 'json' }).subscribe((res: any) => {
      const file = new Blob([res], { type: media.type });
      const fileURL = URL.createObjectURL(file);
      // create a link element, hide it, direct
      // it towards the blob, and then 'click' it programmatically
      let a = document.createElement('a');
      a.href = fileURL;
      a.download = media.original_name || ''; // name of file to download
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      // clean-up
      URL.revokeObjectURL(fileURL);
      document.body.removeChild(a);
    });
  }

  deleteMediaList(mediaListId: string[], albumId: string, libraryId: string): Observable<string[]> {
    const libraryIdPrams = libraryId ? '?target_id=' + libraryId : '';
    const body = { files: mediaListId };
    return this.http
      .delete<string[]>(`${this.apiUrl}${MEDIA_LIBRARY_PATH}/${albumId}/deleteFiles${libraryIdPrams}`, {
        body: body
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(handleError)
      );
  }

  uploadUserAvatarOrCoverMedia(
    place: 'avatar' | 'cover',
    file: FormData
  ): Observable<MediaApiModels.UploadMediaReponse> {
    return this.http.post<MediaApiModels.UploadMediaReponse>(this.apiUrl + `/media/libraries/${place}`, file);
  }

  uploadUserFrameMedia(file: FormData): Observable<MediaApiModels.UploadMediaReponse> {
    return this.http.post<MediaApiModels.UploadMediaReponse>(this.apiUrl + `/media/libraries/uploadFile`, file);
  }

  updateGroupAvatarOrCover(target_id: string, files: FormData): Observable<any> {
    return this.http.post<MediaApiModels.UploadMediaReponse>(
      this.apiUrl + `/media/file/uploadFile?target_id=${target_id}`,
      files
    );
  }

  uploadGroupAvatarOrCover(
    place: 'avatar' | 'cover',
    file: FormData,
    groupId: string | null
  ): Observable<MediaApiModels.UploadMediaReponse> {
    return this.http.post<MediaApiModels.UploadMediaReponse>(
      this.apiUrl + `/media/libraries/${place}?target_id=${groupId}`,
      file
    );
  }
}
